import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { CSSProperties, useEffect, useState, VFC } from "react";
import styled from "styled-components";
import { LegacyImage, Link, Markdown, ModalContactInfo } from "components";
import { Disclosure } from "components/Disclosure";
import { useLockBodyScroll, useWindowSize } from "hooks";
import { FullArrow } from "icons";
import { getImageFormat } from "lib/utils";
import { DataTestId } from "types/enums/testId";
import { FooterContent } from "types/graphql/generated";

interface Props {
  content: FooterContent;
}

const Footer: VFC<Props> = withErrorBoundary(
  ({
    content: {
      disclaimer,
      logo: {
        data: { logo },
      },
      logoMobile: {
        data: { logoMobile },
      },
      categories,
      socialLinks,
      additionalLinks,
      contactUsButtonLabel,
      footer1Modal,
    },
  }) => {
    const [isOpenModal, setIsOpenModal] = useState(false);

    useLockBodyScroll(isOpenModal);

    const { width } = useWindowSize();
    const [plugHeight, setPlugHeight] = useState(0);

    useEffect(() => {
      const page = document.querySelector(".Page");
      const footer = document.querySelector(".Footer");
      const footerInnerContainer = document.querySelector(".Footer__innerContainer");

      if (!page || !footer || !footerInnerContainer) return;

      const { height: pageHeight } = page.getBoundingClientRect();
      const { height: footerHeight } = footer.getBoundingClientRect();
      const { height: footerInnerContainerHeight } = footerInnerContainer.getBoundingClientRect();

      const pageHeightWithoutPlug = pageHeight - footerHeight + footerInnerContainerHeight;

      setPlugHeight(window.innerHeight - pageHeightWithoutPlug);
    }, [width]);

    return (
      <StyledFooter className="Footer" style={{ "--plug-height": `${Math.max(plugHeight, 0)}px` } as CSSProperties}>
        <div className="Footer__innerContainer">
          <Disclosure />
          <div className="Content">
            <div className="Footer__top">
              <div className="Footer__topText">
                <Link href="/" className="Footer__logoContainer">
                  <LegacyImage
                    {...logo}
                    transform={{
                      format: getImageFormat(logo.ext),
                    }}
                    layout="fill"
                    objectFit="contain"
                    objectPosition="left"
                  />
                </Link>
              </div>
              <div data-testid={DataTestId.FooterContainer}>
                <ul className="Footer__categories">
                  {categories.map(({ id, categoryName, footerLinks }) => (
                    <li key={id} className="Footer__category">
                      <h2 className="Font_16_22_700 md:Font_14_20_450 Color_fog40">{categoryName}</h2>
                      <ul className="Footer__categoryLinks">
                        {categoryName === "Company" && (
                          <li>
                            <div className="Footer__modalContainer">
                              <button
                                onClick={() => setIsOpenModal(true)}
                                className="Footer__openModalButton Font_18_26_600 md:Font_16_22_450"
                              >
                                {contactUsButtonLabel}
                              </button>
                              <ModalContactInfo
                                content={footer1Modal}
                                isOpen={isOpenModal}
                                onClose={() => setIsOpenModal(false)}
                              />
                            </div>
                          </li>
                        )}
                        {footerLinks.map(({ id, link, linkLabel, additionalLabel }) => (
                          <li key={id} className="Footer__categorySingleLink">
                            <Link href={link} className="Footer__linkWrapper Font_18_26_600 md:Font_16_22_450">
                              <p>{linkLabel}</p>
                              {additionalLabel && (
                                <div className="Footer__categoryLink">
                                  <FullArrow className="Footer__arrowIcon" />
                                  <p className="Footer__hiring Color_dawn50">{additionalLabel}</p>
                                </div>
                              )}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
              <ul className="Footer__additionalLinksMobile Color_fog40">
                {additionalLinks.map(({ id, link, linkLabel, className }) => (
                  <li key={id} className="Footer__additionalSingleLinkMobile">
                    <Link href={link} className={`${className ?? ""} sm:Font_16_22_600`}>
                      {linkLabel}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="Footer__bottom">
              <Link href="/" className="Footer__logoContainerMobile">
                <LegacyImage
                  {...logoMobile}
                  transform={{
                    format: getImageFormat(logoMobile.ext),
                  }}
                  layout="fill"
                  objectFit="contain"
                />
              </Link>
              <ul className="Footer__socialLinks">
                {socialLinks.map(
                  ({
                    id,
                    link,
                    socialLogo: {
                      data: { logoSocial },
                    },
                  }) => (
                    <li key={id} className="Footer__socialSingleLink">
                      <Link href={link}>
                        <LegacyImage
                          {...logoSocial}
                          transform={{ format: getImageFormat(logoSocial.ext) }}
                          layout="fill"
                          objectFit="contain"
                          className="Footer__socialLogo"
                        />
                      </Link>
                    </li>
                  )
                )}
              </ul>
              <ul className="Footer__additionalLinks">
                {additionalLinks.map(({ id, link, linkLabel, className }) => (
                  <li key={id} className="Footer__additionalSingleLink">
                    <Link href={link} className={`${className ?? ""} Font_14_20_450 Color_fog30`}>
                      {linkLabel}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <Markdown className="Footer__disclaimer Font_14_20_450 Color_fog30">{disclaimer}</Markdown>
          </div>
        </div>
        <div className="Footer__plug" />
      </StyledFooter>
    );
  },
  {}
);

const StyledFooter = styled.footer`
  /* TODO: refactor to avoid disabling */
  /* stylelint-disable no-descending-specificity */
  background-color: ${({ theme }) => theme.colors.fog["80"]};

  /* OneTrust */
  .gdl-ot-sdk-show-settings,
  .ot-sdk-show-settings {
    display: flex;
    align-items: center;

    /* OneTrust icon */
    &::after {
      background-image: url("/images/privacy/ccpa.svg");
      background-size: cover;
      background-repeat: no-repeat;
      content: "";
      height: 15px;
      margin: 0 0 2px 8px;
      width: 30px;
    }
  }

  a,
  button {
    border-radius: 4px;
  }

  .Footer__innerContainer {
    color: white;
    padding-bottom: 64px;
  }

  .Footer__top {
    display: flex;
    justify-content: space-between;
    padding-top: 64px;
    padding-bottom: 64px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.fog["60"]};
  }

  .Footer__logoContainer {
    position: relative;
    width: 32px;
    height: 32px;

    :hover {
      opacity: 0.7;
    }
  }

  .Footer__title {
    margin-top: 24px;
    max-width: 340px;
  }

  .Footer__openModalButton {
    color: ${({ theme }) => theme.colors.fog["00"]};
    cursor: pointer;

    :hover {
      opacity: 0.7;
    }
  }

  .Footer__categories {
    display: flex;
    margin-left: 32px;
  }

  .Footer__category:not(:first-child) {
    margin-left: 80px;
  }

  .Footer__categoryLinks {
    margin-top: 24px;
  }

  .Footer__categoryLink {
    display: flex;
    align-items: center;
  }

  .Footer__hiring,
  .Footer__arrowIcon {
    display: none;
  }

  .Footer__category {
    button {
      display: flex;
    }

    .Footer__categoryLinks {
      .Footer__categorySingleLink {
        .Footer__hiring {
          display: flex;
          margin-left: 4px;
        }

        .Footer__arrowIcon {
          display: flex;
          fill: ${({ theme }) => theme.colors.dawn["50"]};
          width: 16px;
          height: 16px;
        }

        .Footer__linkWrapper {
          display: flex;
          align-items: center;

          p {
            margin-right: 8px;
          }
        }
      }
    }
  }

  .Footer__categorySingleLink {
    margin-top: 16px;
  }

  .Footer__linkWrapper {
    flex-wrap: wrap;

    :hover {
      opacity: 0.7;
    }
  }

  .Footer__bottom {
    display: flex;
    align-items: center;
    margin-top: 16px;
  }

  .Footer__logoContainerMobile {
    display: none;
  }

  .Footer__socialLinks {
    display: flex;
  }

  .Footer__socialSingleLink:not(:first-child) {
    margin-left: 24px;
  }

  .Footer__additionalLinksMobile {
    display: none;
  }

  .Footer__socialLogo {
    position: relative;
    width: 48px;
    height: 48px;

    :hover {
      opacity: 0.7;
    }
  }

  .Footer__additionalLinks {
    display: flex;
    justify-content: space-between;
    margin-left: 48px;
  }

  .Footer__additionalSingleLink {
    :hover {
      opacity: 0.7;
    }
  }

  .Footer__additionalSingleLink:not(:first-child) {
    margin-left: 30px;
  }

  .Footer__disclaimer {
    margin-top: 48px;
    max-width: 1040px;
  }

  .Footer__plug {
    height: var(--plug-height);
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .Footer__category:not(:first-child) {
      margin-left: 64px;
    }

    .Footer__additionalLinks {
      margin-left: 24px;
    }

    .Footer__additionalSingleLink:not(:first-child) {
      margin-left: 16px;
    }
  }

  @media (max-width: 875px) {
    .gdl-ot-sdk-show-settings::after,
    .ot-sdk-show-settings::after {
      margin-bottom: 0;
    }

    .Footer__top {
      justify-content: unset;
      flex-direction: column;
      padding-top: 40px;
      padding-bottom: 48px;
    }

    .Footer__logoContainer {
      display: none;
    }

    .Footer__title {
      display: none;
      max-width: unset;
    }

    .Footer__categories {
      justify-content: space-between;
      margin-left: unset;
      width: 100%;
    }

    .Footer__category:not(:first-child) {
      margin-left: 32px;
    }

    .Footer__additionalSingleLink:not(:first-child) {
      margin-left: 18px;
    }

    .Footer__bottom {
      margin-top: 32px;
      flex-direction: column;
      align-items: unset;
      justify-content: space-between;
    }

    .Footer__logoContainerMobile {
      position: relative;
      display: flex;
      width: 104px;
      height: 33px;
    }

    .Footer__socialLinks {
      margin-top: 24px;
    }

    .Footer__socialSingleLink:not(:first-child) {
      margin-left: 16px;
    }

    .Footer__additionalLinksMobile {
      display: flex;
      flex-direction: column;
      margin-top: 40px;

      button {
        text-decoration: none;
        color: inherit;
      }
    }

    .Footer__additionalLinks {
      display: none;
    }

    .Footer__additionalSingleLinkMobile:not(:first-child) {
      margin-top: 8px;
    }

    .Footer__disclaimer {
      margin-top: 32px;
    }
  }
`;

gql`
  fragment FooterContent on ComponentFooterFooter1 {
    logo {
      data {
        logo: attributes {
          src: hash
          alt: alternativeText
          ext
        }
      }
    }
    logoMobile {
      data {
        logoMobile: attributes {
          src: hash
          alt: alternativeText
          ext
        }
      }
    }
    categories {
      id
      categoryName
      footerLinks {
        id
        link
        linkLabel
        additionalLabel
      }
    }
    socialLinks {
      id
      link
      socialLogo {
        data {
          logoSocial: attributes {
            src: hash
            alt: alternativeText
            ext
          }
        }
      }
    }
    additionalLinks {
      id
      link
      linkLabel
      className
    }
    disclaimer
    contactUsButtonLabel
    footer1Modal {
      ...ModalContent
    }
  }
`;

export { Footer };
export type { Props as Footer1Props };
